import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { API, CompanyDTO, cs_Cull_retrieveAll_order, CullCategoryDTO, CullDTO, NamedIdentityDTO, Order } from 'src/app/services/api/api';
import { ErrorService } from '../../../services/error.service';
import { ModalNavigationService } from '../../../services/gui/modal/modal-navigation.service';
import { UserService } from '../../../services/user.service';
import { Permission } from 'src/app/services/api/permission';
import { TranslateService } from '@ngx-translate/core';
import { CullDTOAndTags } from '../../../models/taggable.model';
import { TagService } from '../../../services/tag.service';
import { OrderOption, SbSplitButton } from '../../../services/global/global-interfaces';
import { PermissionService } from '../../../services/permission.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RequestClass } from '../../../services/global/RequestClass';
import { SbSplitButtonItem, SearchOptions, SimpleListSelectItem } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { RoutingID } from '../../../routes/sb-routing-enum';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { iif, switchMap } from 'rxjs';
import { UtilService } from '../../../services/util.service';

export type CullType = 'TimeCull' | 'ClientCull' | 'ContentCull' | 'PlayerCull' | 'GeoCull' | null;
type SplitButtonAction = 'hide' | 'lock';
type CullDTOAndTagsAndButtons = CullDTOAndTags & SbSplitButton<SplitButtonAction>;

@Component({
  selector: 'sb-page-filter-list',
  templateUrl: './filter-list.component.html',
  styleUrls: ['./filter-list.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [style({ opacity: 0 }), animate(250, style({ opacity: 1 }))]),
      transition(':leave', [animate(500, style({ opacity: 0 }))]),
    ]),
  ],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterListComponent extends RequestClass implements OnInit {
  // injects
  private readonly userService = inject(UserService);
  private readonly modalNavigation = inject(ModalNavigationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly errorHandler = inject(ErrorService);
  private readonly translate = inject(TranslateService);
  protected readonly tagService = inject(TagService);
  protected readonly api = inject(API);
  protected readonly stringTransformation = inject(StringTransformationService);
  protected readonly permissionService = inject(PermissionService);
  protected readonly util = inject(UtilService);
  // globals references imports
  protected readonly cs_Cull_retrieveAll_order = cs_Cull_retrieveAll_order;

  // imports
  @Input() isInModalContext = false;
  @Input() isInIntegratedContext = false;
  @Input() preselectType: CullType = null;
  @Input() preselectedCompany: NamedIdentityDTO = null;

  @Output() selectedFilter = new EventEmitter<CullDTO>();

  /* filter variables */
  // search
  search: string | null = null;
  exactMatch: SearchOptions = 'contains';
  // Id filtering
  selectedCompany: NamedIdentityDTO | null = null;
  selectedCategory: CullCategoryDTO | null = null;
  // threeWay-toggles
  isLocked: boolean | null = null;
  hasCategory: boolean | null = null;
  isInUse: boolean | null = null;
  isCurrentlyInUse: boolean | null = null;
  // type filter
  selectedType: CullType = null;
  // config
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    button: {
      visibleWithoutChanges: true,
    },
  };
  // items
  cullTypes: SimpleListSelectItem[] = [
    {
      value: 'PlayerCull',
      label: this.translate.instant('lang.culls.culltype.player'),
    },
    {
      value: 'TimeCull',
      label: this.translate.instant('lang.culls.culltype.time'),
    },
    {
      value: 'ClientCull',
      label: this.translate.instant('lang.culls.culltype.client'),
    },
    {
      value: 'ContentCull',
      label: this.translate.instant('lang.culls.culltype.content'),
    },
    {
      value: 'GeoCull',
      label: this.translate.instant('lang.culls.culltype.geo'),
    },
  ];

  // ordering
  allOrderOptions: OrderOption<cs_Cull_retrieveAll_order>[] = [
    { value: cs_Cull_retrieveAll_order.NAME, direction: Order.asc, label: this.translate.instant('lang.globals.title') },
    { value: cs_Cull_retrieveAll_order.ID, direction: Order.asc, label: this.translate.instant('lang.globals.id') },
    { value: cs_Cull_retrieveAll_order.CREATED_AT, direction: Order.asc, label: this.translate.instant('lang.globals.createdAt') },
    { value: cs_Cull_retrieveAll_order.MODIFIED_AT, direction: Order.asc, label: this.translate.instant('lang.globals.modifiedAt') },
    { value: cs_Cull_retrieveAll_order.USAGE_COUNT, direction: Order.asc, label: this.translate.instant('lang.globals.usageCount') },
    {
      value: cs_Cull_retrieveAll_order.CURRENT_USAGE_COUNT,
      direction: Order.asc,
      label: this.translate.instant('lang.deviceFilterList.currentUsageCount'),
    },
  ] as const;
  orderOption = signal<OrderOption<cs_Cull_retrieveAll_order>>({ ...this.allOrderOptions[0] });

  // global
  elements: CullDTOAndTagsAndButtons[] = [];
  showAllFilter: boolean = false;

  ngOnInit() {
    if (this.isInModalContext) {
      const preselectedType = this.modalNavigation.getCurrentNavigation().data.preselectType;
      if (preselectedType) {
        this.preselectType = preselectedType;
      }
    }

    if (this.isInModalContext || this.isInIntegratedContext) {
      this.filterConfig.ignoreQueryParams = true;
    }
    if (this.preselectType) {
      this.selectedType = this.preselectType;
    }

    if (!this.isInModalContext) {
      this.userService.selectedCompany.subscribe((company: CompanyDTO) => {
        this.selectedCompany = company;
        if (company) {
          this.preselectedCompany = null;
        }
        this.requestDebounced();
      });
    }
  }

  // order methods
  resetOrderOption() {
    // reset to default ordering, copy object properties to avoid changing original default
    this.orderOption.set({ ...this.allOrderOptions[0] });
  }

  setToOrderAndRefresh(pValue: cs_Cull_retrieveAll_order) {
    this.setToOrderOption(pValue);
    this.requestDebounced();
  }

  private setToOrderOption(pValue: cs_Cull_retrieveAll_order) {
    const foundOrderOption = this.allOrderOptions.find(option => option.value === pValue);
    if (!pValue || !foundOrderOption) {
      // fallback to default if nothing is set or orderOption cannot be found in allOrderOptions
      this.resetOrderOption();
      return;
    }
    const currentOrderOption = this.orderOption();
    if (currentOrderOption.value === pValue) {
      if (currentOrderOption.direction === Order.asc) {
        this.orderOption.set({ ...currentOrderOption, direction: Order.desc });
      } else {
        this.orderOption.set({ ...currentOrderOption, direction: Order.asc });
      }
    } else {
      this.orderOption.set({ ...foundOrderOption });
    }
  }

  getOrder(): { by: cs_Cull_retrieveAll_order; direction: Order } {
    // if no orderOption -> return default order
    if (!this.orderOption().value) {
      this.resetOrderOption();
    }
    // if value equals column title or order option has been restored
    return {
      by: this.orderOption().value,
      direction: this.orderOption().direction,
    };
  }

  // request methods
  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }

    let managedCompany: number | null;
    let managedCompanyExclusive = null;
    if (this.isInIntegratedContext || this.isInModalContext) {
      managedCompany = this.preselectedCompany ? this.preselectedCompany.id : null;
    } else {
      managedCompany = this.selectedCompany ? this.selectedCompany.id : null;
      managedCompanyExclusive = this.preselectedCompany ? this.preselectedCompany.id : null;
    }
    this.api
      .cs_Cull_retrieveAll(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          type: this.selectedType,
          category: this.selectedCategory ? [this.selectedCategory.id] : null,
          company: [managedCompanyExclusive],
          forCompany: managedCompany,
          hasCategory: this.hasCategory,
          isLocked: this.isLocked,
          inUse: this.isInUse,
          currentlyInUse: this.isCurrentlyInUse,
          showHidden: this.isInIntegratedContext || this.isInModalContext || this.showAllFilter,
          order: this.getOrder(),
        },
        this.pagination
      )
      .pipe(
        take(1),
        switchMap(culls => this.tagService.getAndAddTagEntitiesObs(this.tagService.TaggableDTO.Cull, culls)),
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: e => {
          this.elements = this.buildSplitButtons<CullDTOAndTags>(e);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  buildSplitButtons<T extends { id: any; global: boolean; type: string; lockedBy: NamedIdentityDTO | null }>(
    itemsToAdd: T[]
  ): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.cull_show])) {
        item.sbButtons.push({
          icon: 'details',
          tooltip: 'lang.globals.details',
          link: RoutingID.filterDetails,
          pathParams: [{ id: 'filterID', value: item?.id }],
        });
      }

      if (this.permissionService.hasPermissions([this.permissionService.Permission.cull_edit])) {
        item.sbButtons.push({
          icon: 'edit',
          tooltip: 'lang.globals.edit',
          link: RoutingID.filterEdit,
          pathParams: [{ id: 'filterID', value: item?.id }],
          disabled: item.global || !!this.preselectType || this.isInModalContext || !this.hasEditRight(item.type as CullType),
        });
      }

      if (this.permissionService.hasPermissions([this.permissionService.Permission.cull_lock])) {
        if (!item.lockedBy) {
          item.sbButtons.push({
            icon: 'lockClosed',
            tooltip: 'lang.globals.lock',
            iconClass: 'hover:!tw-bg-sbRed-500 *:tw-text-sbRed-500',
            action: 'lock',
            disabled: item.global, // global is not lockable
          });
        } else {
          item.sbButtons.push({
            icon: 'lockOpen',
            tooltip: 'lang.globals.unlock',
            iconClass: 'hover:!tw-bg-sbGreen-500 *:tw-text-sbGreen-500',
            action: 'lock',
            disabled:
              item.global || // global is not unlockable
              (item.lockedBy.id !== this.userService.user.user.id && // either i locked it
                !this.permissionService.hasPermission(this.permissionService.Permission.cull_lock_admin)), // or i can unlock all
          });
        }
      }
    });
    return result;
  }

  resolveSplitButtonAction(cull: CullDTOAndTagsAndButtons, $event: SplitButtonAction) {
    switch ($event) {
      case 'lock':
        this.toggleLockFilter(cull);
        break;
      case 'hide':
        this.toggleHideFilter(cull);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  selectFilter(filter: CullDTO) {
    if (this.isInModalContext) {
      this.modalNavigation.result(filter);
    } else {
      this.selectedFilter.emit(filter);
    }
  }

  hasEditRight(cullType: CullType): boolean {
    switch (cullType) {
      case 'TimeCull':
        return this.permissionService.hasPermissions([Permission.cull_time_edit]);
      case 'ClientCull':
        return this.permissionService.hasPermissions([Permission.cull_client_edit]);
      case 'GeoCull':
        return this.permissionService.hasPermissions([Permission.cull_geo_edit]);
      case 'PlayerCull':
        return this.permissionService.hasPermissions([Permission.cull_player_edit]);
      case 'ContentCull':
        return this.permissionService.hasPermissions([Permission.cull_content_edit]);
      default:
        return false;
    }
  }

  toggleHideFilter(filter: CullDTO) {
    filter.hidden = !filter.hidden;
    this.api
      .cs_Cull_setHidden(filter.id, { hidden: filter.hidden })
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: () => {
          this.requestDebounced();
        },
      });
  }

  toggleLockFilter(filter: CullDTO) {
    if (!filter) {
      return;
    }
    this.loading.set(true);
    const lockStatus: boolean = !filter.lockedBy;
    iif(
      () => this.permissionService.hasPermission(this.permissionService.Permission.cull_lock_admin),
      this.api.cs_Cull_setLockAdmin(filter.id, { unlock: !lockStatus }),
      this.api.cs_Cull_setLock(filter.id, { unlock: !lockStatus })
    )
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.requestDebounced();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  // filter methods
  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    // display
    this.showAllFilter = filterMap.get('hidden')?.[0];
    // threeWay-toggles
    this.isLocked = filterMap.get('isLocked')?.[0];
    this.hasCategory = filterMap.get('hasCategory')?.[0];
    this.isInUse = filterMap.get('isInUse')?.[0];
    this.isCurrentlyInUse = filterMap.get('isCurrentlyInUse')?.[0];
    // type filter
    this.selectedType = filterMap.get('cullType')?.[0];
    // id filter
    this.selectedCompany = filterMap.get('companies')?.[0];
    this.selectedCategory = filterMap.get('categories')?.[0];
    // search
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
    // order
    const orderOption = filterMap.get('orderOption')?.[0];
    const orderDirection = filterMap.get('orderDirection')?.[0];
    this.setOrderOptionFromPanel(orderOption, orderDirection);
  }

  private setOrderOptionFromPanel(value: cs_Cull_retrieveAll_order, pDirection: Order) {
    const foundOrderOption = this.allOrderOptions.find(option => option.value === value);
    if (!foundOrderOption) {
      // fallback to default if nothing is set or orderOption cannot be found in allOrderOptions
      this.resetOrderOption();
      return;
    }
    this.orderOption.set({ ...foundOrderOption, direction: pDirection });
  }
}
